// OAuth コールバック。code を交換して所属に応じて遷移する（portal.md「ログイン（ポータル自身）」）。
//
// Pre Sign-up トリガーが IdP の本人性を紐付けた直後の初回サインインは Cognito 側で失敗する
// （error=linked_retry 相当）。その場合は同じ authorize 要求を 1 回だけやり直す（ADR-0015）。

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { signIn, userManager, type SignInState } from "../lib/auth";
import { joinedContractName, landingPath } from "../lib/landing";
import { REMEMBER_KEY } from "../lib/login";

const RETRY_KEY = "portal.linkedRetry";

type Outcome = { path: string; joined: string | null } | { error: string } | { redirecting: true };

// code は 1 回しか交換できない。StrictMode の effect 二重実行で 2 回目の交換が失敗し、
// 成功しているのに一瞬エラー画面が出ないよう、同じコールバック URL は 1 回だけ処理する。
let handled: { search: string; outcome: Promise<Outcome> } | null = null;

function handleCallback(): Promise<Outcome> {
  const search = window.location.search;
  if (handled?.search !== search) handled = { search, outcome: process(search) };
  return handled.outcome;
}

async function process(search: string): Promise<Outcome> {
  const q = new URLSearchParams(search);
  const err = q.get("error_description") ?? q.get("error");
  if (err) {
    if (/linked_retry|PreSignUp/i.test(err) && !sessionStorage.getItem(RETRY_KEY)) {
      sessionStorage.setItem(RETRY_KEY, "1");
      // 直前に共通ログイン画面で入れた email を渡し、メールアドレスの入力を繰り返させない。
      let hint: string | undefined;
      try { hint = localStorage.getItem(REMEMBER_KEY) ?? undefined; } catch { /* 無くてもやり直せる */ }
      await signIn(hint);
      return { redirecting: true };
    }
    return { error: err };
  }
  try {
    const user = await userManager().signinCallback();
    sessionStorage.removeItem(RETRY_KEY);
    // 所属が 1 つなら契約選択画面を経ずに TOP へ。/me が失敗したら選択画面がエラーを出す。
    // 招待のリンクから来て選択画面へ行くときは、参加した契約を選択画面に示す。
    const me = await api.me().catch(() => null);
    const invited = (user?.state as SignInState | undefined)?.invitedContractId;
    return { path: me ? landingPath(me) : "/select", joined: me ? joinedContractName(me, invited) : null };
  } catch (e) {
    return { error: String(e) };
  }
}

export function CallbackPage() {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    handleCallback().then((o) => {
      if (!active) return;
      if ("path" in o) nav(o.path, { replace: true, state: o.joined ? { joined: o.joined } : undefined });
      else if ("error" in o) setError(o.error);
    });
    return () => { active = false; };
  }, [nav]);

  return (
    <main className="narrow">
      {error ? (
        <>
          <h1>ログインできませんでした</h1>
          <p className="error">{error}</p>
          <p><a href="/login">ログインをやり直す</a></p>
        </>
      ) : <p className="muted">ログイン処理中…</p>}
    </main>
  );
}
